import React from 'react';
import { RefreshCw } from 'lucide-react';
import {
  AgnesModal,
  AgnesButton,
  AgnesAlert,
  AgnesSwitch,
  AgnesTabs,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTabsContent,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { useColetaConfigStore, type CertidaoConfig } from '@/stores/useColetaConfigStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import {
  CERTIDAO_LABEL,
  PERIODICIDADES,
  PERIODICIDADE_LABEL,
} from '@/services/ServiceColetaConfig';

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const fmt = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-BR') : '-');

function CardCertidao({
  cfg,
  onChange,
  coletando,
  bloqueado,
  somenteLeitura,
  onColetarAgora,
}: {
  cfg: CertidaoConfig;
  onChange: (c: CertidaoConfig) => void;
  coletando: boolean;
  bloqueado: boolean;
  somenteLeitura: boolean;
  onColetarAgora: () => void;
}) {
  // Só toggle + periodicidade: o trigger da coleta AUTOMÁTICA é a cadência. Os antigos campos
  // numéricos (dias antes vencimento / meses / dias retroativos) não afetam a coleta (DCTF = 60
  // competências, Mensagens e-CAC = 6 meses fixos, CRF/CND sem parâmetro no agnaldo) — removidos
  // do modal. "Coletar agora" dispara a coleta na hora, fora da cadência (não exige habilitado).
  return (
    <div className="rounded-xl border border-agnes-border p-4">
      <div className="flex items-start justify-between gap-3">
        <AgnesSwitch
          label={CERTIDAO_LABEL[cfg.certidao as keyof typeof CERTIDAO_LABEL]}
          hint="Habilitar coleta"
          checked={cfg.enabled}
          disabled={somenteLeitura}
          onCheckedChange={(v) => onChange({ ...cfg, enabled: v })}
        />
        <AgnesButton
          variant="outlined"
          size="sm"
          disabled={bloqueado || somenteLeitura}
          onClick={onColetarAgora}
          startIcon={<RefreshCw className={`h-4 w-4 ${coletando ? 'animate-spin' : ''}`} />}
        >
          {coletando ? 'Coletando...' : 'Coletar agora'}
        </AgnesButton>
      </div>
      {cfg.certidao === 'ECAC_MESSAGES' && (
        <AgnesAlert preset="warning" className="mt-3" data-qa="ecac-indisponivel-alerta">
          A coleta de Mensagens e-CAC pode estar indisponível. As consultas à caixa postal da
          Receita Federal estão sendo recusadas; quando isso ocorre, a sincronização falha e nenhuma
          mensagem nova é trazida.
        </AgnesAlert>
      )}
      <div className="mt-3 sm:max-w-xs">
        <span className="mb-1 block text-xs text-agnes-text-secondary">Periodicidade *</span>
        <AgnesSelect
          value={cfg.periodicidade ?? ''}
          onValueChange={(v) => onChange({ ...cfg, periodicidade: v })}
          disabled={!cfg.enabled || somenteLeitura}
        >
          <AgnesSelectTrigger className={SELECT_TRIGGER}>
            <AgnesSelectValue placeholder="Selecione" />
          </AgnesSelectTrigger>
          <AgnesSelectContent>
            {PERIODICIDADES.map((o) => (
              <AgnesSelectItem key={o.value} value={o.value}>
                {o.label}
              </AgnesSelectItem>
            ))}
          </AgnesSelectContent>
        </AgnesSelect>
      </div>
    </div>
  );
}

export default function ColetaAutomaticaModal() {
  const rootCnpj = useColetaConfigStore((s) => s.rootCnpj);
  const nome = useColetaConfigStore((s) => s.nome);
  const configs = useColetaConfigStore((s) => s.configs);
  const historico = useColetaConfigStore((s) => s.historico);
  const carregando = useColetaConfigStore((s) => s.carregando);
  const salvando = useColetaConfigStore((s) => s.salvando);
  const coletandoAgora = useColetaConfigStore((s) => s.coletandoAgora);
  const podeEditar = usePermissionsStore((s) =>
    s.hasPermission(MENU_PERMISSIONS.CONFIG_EMPRESAS_WRITE),
  );

  const [tab, setTab] = React.useState('edit');
  const [local, setLocal] = React.useState<CertidaoConfig[]>([]);

  // ressincroniza a cópia editável quando o store recarrega (abrir / pós-save)
  React.useEffect(() => {
    setLocal(configs);
  }, [configs]);
  React.useEffect(() => {
    if (rootCnpj) setTab('edit');
  }, [rootCnpj]);

  const open = rootCnpj !== null;
  const fechar = () => useColetaConfigStore.getState().fechar();
  const setCard = (i: number) => (c: CertidaoConfig) =>
    setLocal((prev) => prev.map((x, idx) => (idx === i ? c : x)));
  const podeSalvar = !salvando && !local.some((c) => c.enabled && !c.periodicidade);

  return (
    <AgnesModal
      open={open}
      title="Coleta automática de dados"
      maxWidth="lg"
      onClose={fechar}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={fechar}>
            {tab === 'historic' ? 'Fechar' : 'Cancelar'}
          </AgnesButton>
          {tab === 'edit' && podeEditar && (
            <AgnesButton
              variant="primary"
              disabled={!podeSalvar}
              onClick={async () => {
                await useColetaConfigStore.getState().salvar(local);
              }}
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </AgnesButton>
          )}
        </div>
      }
    >
      <AgnesTabs
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          if (v === 'historic') useColetaConfigStore.getState().carregarHistorico();
        }}
      >
        <AgnesTabsList>
          <AgnesTabsTrigger value="edit">Editar</AgnesTabsTrigger>
          <AgnesTabsTrigger value="historic">Histórico</AgnesTabsTrigger>
        </AgnesTabsList>

        <AgnesTabsContent value="edit">
          {carregando ? (
            <div className="py-8 text-center text-agnes-text-secondary">Carregando…</div>
          ) : (
            <div className="flex flex-col gap-4">
              <AgnesAlert preset="warning">
                <b>As configurações a seguir podem gerar custos adicionais.</b> Antes de realizar
                mudanças, certifique-se de verificar o pacote contratado.
              </AgnesAlert>
              <p className="text-xs text-agnes-text-secondary">
                Certificado digital: <b>{nome || '-'}</b> (somente leitura, configure no Agnes
                Identidade).
              </p>
              {local.map((cfg, i) => (
                <CardCertidao
                  key={cfg.certidao}
                  cfg={cfg}
                  onChange={setCard(i)}
                  coletando={coletandoAgora === cfg.certidao}
                  bloqueado={coletandoAgora !== null}
                  somenteLeitura={!podeEditar}
                  onColetarAgora={() => useColetaConfigStore.getState().coletarAgora(cfg.certidao)}
                />
              ))}
            </div>
          )}
        </AgnesTabsContent>

        <AgnesTabsContent value="historic">
          {historico.length === 0 ? (
            <p className="py-8 text-center text-sm text-agnes-text-secondary">
              Nenhuma alteração registrada.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {}
              {historico.map((h: any) => (
                <div key={h.id} className="rounded-lg border border-agnes-border p-3 text-sm">
                  <div className="flex justify-between text-xs text-agnes-text-secondary">
                    <span>{fmt(h.createdAt)}</span>
                    <span>por {h.changedBy ?? '-'}</span>
                  </div>
                  <div className="mt-1">
                    <b>{CERTIDAO_LABEL[h.certidao as keyof typeof CERTIDAO_LABEL] ?? h.certidao}</b>{' '}
                    - {h.enabled ? 'Habilitado' : 'Desabilitado'}
                    {h.enabled && h.periodicidade
                      ? ` · ${PERIODICIDADE_LABEL[h.periodicidade as keyof typeof PERIODICIDADE_LABEL] ?? h.periodicidade}`
                      : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </AgnesTabsContent>
      </AgnesTabs>
    </AgnesModal>
  );
}
